import React from 'react';
import { Icon } from '@/shared/icons';
import type { HistoryImportRun } from '@troia-v3/types';

export interface QrStepQrData {
  qrCode: string;
  expires: number;
}

export interface QrStepProps {
  /** Run atual (pode estar `undefined` no instante entre iniciar a sessão e o primeiro fetch). */
  run: HistoryImportRun | undefined;
  /** Último QR recebido via socket (`history-import:qr`) — sempre o mais recente. */
  qr: QrStepQrData | undefined;
}

/**
 * Passo 2 do wizard — exibe o QR Code (`run.status === 'awaiting_qr'`) ou,
 * assim que o telefone conecta e o worker começa a puxar o histórico
 * (`run.status === 'capturing'`), troca para um spinner com os contadores
 * de chats/mensagens encontrados (via `useImportRun` polling + merge de
 * socket).
 */
export const QrStep: React.FC<QrStepProps> = ({ run, qr }) => {
  if (run?.status === 'capturing') {
    return (
      <div className="flex flex-col items-center gap-4 py-8" data-testid="import-capturing-state">
        <Icon name="loader" className="w-8 h-8 animate-spin" style={{ color: '#8b5cf6' }} />
        <p className="text-sm font-medium" style={{ color: '#1e293b' }}>
          Capturando histórico do WhatsApp...
        </p>
        <p className="text-xs text-center max-w-xs" style={{ color: '#6b7280' }}>
          Isso pode levar alguns minutos, dependendo da quantidade de conversas.
        </p>
        <div className="grid grid-cols-2 gap-3 w-full">
          <div className="rounded-lg border p-3 text-center" style={{ borderColor: '#e5e7eb' }}>
            <p
              className="text-xl font-semibold"
              style={{ color: '#1e293b' }}
              data-testid="import-capturing-chats-count"
            >
              {run.counters.chatsFound}
            </p>
            <p className="text-xs" style={{ color: '#6b7280' }}>
              Conversas encontradas
            </p>
          </div>
          <div className="rounded-lg border p-3 text-center" style={{ borderColor: '#e5e7eb' }}>
            <p
              className="text-xl font-semibold"
              style={{ color: '#1e293b' }}
              data-testid="import-capturing-messages-count"
            >
              {run.counters.messagesFound}
            </p>
            <p className="text-xs" style={{ color: '#6b7280' }}>
              Mensagens encontradas
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (!qr) {
    return (
      <div className="flex flex-col items-center gap-3 py-12" data-testid="import-qr-loading">
        <Icon name="loader" className="w-8 h-8 animate-spin" style={{ color: '#8b5cf6' }} />
        <p className="text-sm" style={{ color: '#6b7280' }}>
          Gerando QR Code...
        </p>
      </div>
    );
  }

  return (
    <div className="text-center space-y-4" data-testid="import-qr-state">
      <p className="text-sm font-medium" style={{ color: '#1e293b' }}>
        Escaneie o QR Code para conectar
      </p>

      <div
        className="inline-block p-4 rounded-lg border"
        style={{ borderColor: '#e5e7eb', backgroundColor: '#ffffff' }}
      >
        <img src={qr.qrCode} alt="QR Code WhatsApp" className="w-64 h-64" data-testid="import-qr-image" />
      </div>

      <div className="text-sm text-left mx-auto max-w-xs space-y-0.5" style={{ color: '#64748b' }}>
        <p>1. Abra o WhatsApp no seu celular</p>
        <p>2. Toque em Mais opções (⋮) → Aparelhos conectados</p>
        <p>3. Toque em Conectar um aparelho</p>
        <p>4. Aponte seu celular para esta tela</p>
      </div>

      <p className="text-xs" style={{ color: '#94a3b8' }} data-testid="import-qr-expires">
        QR Code expira em: {new Date(qr.expires).toLocaleTimeString('pt-BR')}
      </p>
      <p className="text-xs italic" style={{ color: '#94a3b8' }} data-testid="import-qr-waiting">
        Aguardando leitura...
      </p>
    </div>
  );
};

QrStep.displayName = 'QrStep';
